import React from "react";
import {
  ClipboardCheck,
  PencilRuler,
  FileSignature,
  Wrench,
  PlugZap,
  HeartHandshake,
} from "lucide-react";
import { useTranslation } from "@/context/LanguageContext";

const ICONS = [ClipboardCheck, PencilRuler, FileSignature, Wrench, PlugZap, HeartHandshake];
const TONES = [
  { tone: "from-yellow-100 to-yellow-50", iconBg: "bg-yellow-400" },
  { tone: "from-lime-100 to-emerald-50", iconBg: "bg-lime-500" },
  { tone: "from-green-100 to-green-50", iconBg: "bg-green-600" },
  { tone: "from-emerald-100 to-emerald-50", iconBg: "bg-emerald-600" },
  { tone: "from-green-100 to-lime-50", iconBg: "bg-green-700" },
  { tone: "from-yellow-100 to-green-50", iconBg: "bg-yellow-500" },
];

export default function WorkflowSection() {
  const { t } = useTranslation();
  const steps = t("workflow.steps");
  return (
    <section
      id="workflow"
      data-testid="workflow-section"
      className="py-20 md:py-28 bg-stone-50 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 relative">
        <div className="max-w-2xl">
          <span className="text-green-700 font-semibold tracking-widest text-xs uppercase">
            {t("workflow.eyebrow")}
          </span>
          <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl text-stone-900">
            {t("workflow.title_pre")}{" "}
            <span className="font-handwritten text-green-700 text-5xl lg:text-6xl">
              {t("workflow.title_hl")}
            </span>{" "}
            {t("workflow.title_post")}
          </h2>
          <p className="mt-4 text-stone-600 leading-relaxed">
            {t("workflow.subtitle")}
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((s, i) => {
            const Icon = ICONS[i];
            const { tone, iconBg } = TONES[i];
            return (
              <div
                key={i}
                data-testid={`workflow-step-${i + 1}`}
                className={`step-card rounded-3xl border border-stone-200 bg-gradient-to-br ${tone} p-6 relative`}
              >
                <div className="absolute top-5 right-5 font-handwritten text-5xl text-stone-300 select-none">
                  0{i + 1}
                </div>
                <div className={`w-16 h-16 rounded-2xl ${iconBg} grid place-items-center shadow-lg rotate-[-4deg]`}>
                  <Icon className="w-8 h-8 text-white" strokeWidth={2.2} />
                </div>
                <h3 className="mt-5 font-display font-bold text-xl text-stone-900">
                  {s.title}
                </h3>
                <div className="mt-4 space-y-3 text-sm">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
                      {t("workflow.activities")}
                    </div>
                    <div className="text-stone-700">{s.activities}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
                      {t("workflow.deliverables")}
                    </div>
                    <div className="text-stone-700">{s.deliverables}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
                      {t("workflow.outcome")}
                    </div>
                    <div className="text-stone-900 font-semibold">{s.outcome}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
